import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Brand } from "@/components/Brand";
import { ViewNav } from "@/components/ViewNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wrench, Plus, Loader2, Pencil, Trash2, QrCode, Package } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const today = () => new Date().toISOString().slice(0, 10);
const emptyForm = (siteKey) => ({ site: siteKey || "", asset_id: "none", job_description: "", date_completed: today(), performed_by: "", status: "todo" });

const STATUS_META = {
  todo: { label: "To Do", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  in_progress: { label: "In Progress", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  completed: { label: "Completed", cls: "bg-green-50 text-green-700 border-green-200" },
};

export default function Maintenance() {
  const { user } = useAuth();
  const restrictedKey = user && user.site_access && user.site_access !== "all" ? user.site_access.toUpperCase() : null;
  const [sites, setSites] = useState([]);
  const [assets, setAssets] = useState([]);
  const [logs, setLogs] = useState([]);
  const [siteFilter, setSiteFilter] = useState(restrictedKey || "ALL");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setLogs(await api.listMaintenance(siteFilter)); }
    finally { setLoading(false); }
  }, [siteFilter]);

  useEffect(() => { api.getSites().then(setSites).catch(() => {}); api.listAssets("ALL").then(setAssets).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const siteOpts = useMemo(() => {
    const base = sites.map((s) => ({ key: s.key, name: s.name }));
    return restrictedKey ? base.filter((s) => s.key === restrictedKey) : base;
  }, [sites, restrictedKey]);

  const displayed = logs.filter((l) => statusFilter === "all" || (l.status || "completed") === statusFilter);
  const dialogAssets = dialog ? assets.filter((a) => a.site_key === dialog.form.site) : [];

  const openAdd = () => setDialog({ mode: "add", form: emptyForm(restrictedKey || siteOpts[0]?.key || "") });
  const openEdit = (l) => setDialog({ mode: "edit", id: l.id, form: { site: l.site_key, asset_id: l.asset_id || "none", job_description: l.job_description, date_completed: l.date_completed || today(), performed_by: l.performed_by || "", status: l.status || "completed" } });
  const setF = (k, v) => setDialog((d) => {
    const next = { ...d.form, [k]: v };
    if (k === "site") next.asset_id = "none"; // reset asset when site changes
    return { ...d, form: next };
  });

  const changeStatus = async (l, status) => {
    try { await api.updateMaintenance(l.id, { status }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const save = async () => {
    const f = dialog.form;
    if (!f.site) { toast.error("Select a site"); return; }
    if (!f.job_description.trim()) { toast.error("Describe the job"); return; }
    const completed = f.status === "completed";
    const payload = {
      site: f.site, job_description: f.job_description, status: f.status,
      asset_id: f.asset_id && f.asset_id !== "none" ? f.asset_id : undefined,
      date_completed: completed ? f.date_completed : "",
      performed_by: completed ? f.performed_by : "",
    };
    setBusy(true);
    try {
      if (dialog.mode === "add") { await api.createMaintenance(payload); toast.success(completed ? "Maintenance logged" : "Job added to the list"); }
      else { await api.updateMaintenance(dialog.id, payload); toast.success("Log updated"); }
      setDialog(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (l) => {
    if (!window.confirm(`Delete this ${l.site} maintenance log?`)) return;
    try { await api.deleteMaintenance(l.id); toast.success("Log deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const fmtDate = (ymd) => ymd ? new Date(ymd + "T00:00:00").toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  const StatusSelect = ({ l, idSuffix = "" }) => {
    const st = l.status || "completed";
    return (
      <Select value={st} onValueChange={(v) => changeStatus(l, v)}>
        <SelectTrigger className={`h-7 w-[130px] text-xs border ${STATUS_META[st].cls}`} data-testid={`maintenance-status-${l.id}${idSuffix}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="todo">To Do</SelectItem>
          <SelectItem value="in_progress">In Progress</SelectItem>
          <SelectItem value="completed">Completed</SelectItem>
        </SelectContent>
      </Select>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="max-w-[1500px] mx-auto px-3 sm:px-5 py-3 flex items-center justify-between gap-3">
          <Brand subtitle="Maintenance Log" />
          <ViewNav />
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-3 sm:px-5 py-6 space-y-5" data-testid="maintenance-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Wrench className="h-6 w-6 text-orange-600" /> Maintenance &amp; Repairs
          </h2>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px] h-9 bg-white" data-testid="maintenance-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="todo">To Do</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            {!restrictedKey && (
              <Select value={siteFilter} onValueChange={setSiteFilter}>
                <SelectTrigger className="w-[150px] h-9 bg-white" data-testid="maintenance-site-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All sites</SelectItem>
                  {sites.map((s) => <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Button data-testid="maintenance-add-btn" onClick={openAdd} className="h-9 gap-2 bg-orange-600 hover:bg-orange-700"><Plus className="h-4 w-4" /> Add Job</Button>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading logs...</div>
          ) : displayed.length === 0 ? (
            <div className="text-center py-16 text-slate-400">No maintenance jobs for this view.</div>
          ) : (
            <>
              {/* Desktop table */}
              <table className="w-full text-sm hidden md:table" data-testid="maintenance-table">
                <thead>
                  <tr className="text-xs uppercase text-slate-500 text-left border-b border-slate-200 bg-slate-50">
                    <th className="py-2.5 px-4">Status</th>
                    <th className="px-2">Date Done</th>
                    <th className="px-2">Site</th>
                    <th className="px-2">Asset</th>
                    <th className="px-2">Job Description</th>
                    <th className="px-2">Performed By</th>
                    <th className="px-2">Source</th>
                    <th className="px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {displayed.map((l) => {
                    const st = l.status || "completed";
                    return (
                      <tr key={l.id} data-testid={`maintenance-row-${l.id}`} className="border-b border-slate-100 align-top hover:bg-slate-50/60">
                        <td className="py-3 px-4 whitespace-nowrap"><StatusSelect l={l} /></td>
                        <td className="px-2 whitespace-nowrap font-mono-nums text-slate-700">{st === "completed" ? fmtDate(l.date_completed) : "—"}</td>
                        <td className="px-2"><span className="text-xs rounded-full px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200">{l.site}</span></td>
                        <td className="px-2 text-slate-600 whitespace-nowrap">{l.asset_name ? <span className="inline-flex items-center gap-1"><Package className="h-3.5 w-3.5 text-slate-400" />{l.asset_name}</span> : "—"}</td>
                        <td className="px-2 text-slate-800 max-w-md py-3">{l.job_description}</td>
                        <td className="px-2 text-slate-600 whitespace-nowrap">{l.performed_by || "—"}</td>
                        <td className="px-2">{l.source === "qr" ? <span className="inline-flex items-center gap-1 text-xs text-violet-600"><QrCode className="h-3.5 w-3.5" /> QR</span> : <span className="text-xs text-slate-400">Staff</span>}</td>
                        <td className="px-4 text-right whitespace-nowrap">
                          <button onClick={() => openEdit(l)} className="text-slate-400 hover:text-blue-600 p-1" data-testid={`maintenance-edit-${l.id}`}><Pencil className="h-4 w-4" /></button>
                          <button onClick={() => del(l)} className="text-slate-300 hover:text-red-500 p-1" data-testid={`maintenance-delete-${l.id}`}><Trash2 className="h-4 w-4" /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-slate-100">
                {displayed.map((l) => {
                  const st = l.status || "completed";
                  return (
                    <div key={l.id} data-testid={`maintenance-card-${l.id}`} className="p-4 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <StatusSelect l={l} idSuffix="-m" />
                        <div className="flex items-center">
                          <button onClick={() => openEdit(l)} className="text-slate-400 hover:text-blue-600 p-1.5" data-testid={`maintenance-edit-${l.id}-m`}><Pencil className="h-4 w-4" /></button>
                          <button onClick={() => del(l)} className="text-slate-300 hover:text-red-500 p-1.5" data-testid={`maintenance-delete-${l.id}-m`}><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </div>
                      <p className="text-slate-800 text-sm">{l.job_description}</p>
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="rounded-full px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200">{l.site}</span>
                        {l.asset_name && <span className="inline-flex items-center gap-1 text-slate-600"><Package className="h-3.5 w-3.5 text-slate-400" />{l.asset_name}</span>}
                        {l.source === "qr" && <span className="inline-flex items-center gap-1 text-violet-600"><QrCode className="h-3.5 w-3.5" /> QR</span>}
                      </div>
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <span>{st === "completed" ? fmtDate(l.date_completed) : "Not done yet"}</span>
                        <span>{l.performed_by || ""}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <p className="text-sm text-slate-400">Contractors can log jobs without signing in by scanning each asset's QR label (generate them in Admin → Assets &amp; QR).</p>
      </main>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="maintenance-dialog" className="max-w-lg max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{dialog?.mode === "edit" ? "Edit Maintenance Log" : "Add Maintenance Job"}</DialogTitle>
            <DialogDescription>Record a repair or servicing job, or add one to the to-do list.</DialogDescription>
          </DialogHeader>
          {dialog && (
            <div className="space-y-3 py-1">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Status</Label>
                  <Select value={dialog.form.status} onValueChange={(v) => setF("status", v)}>
                    <SelectTrigger data-testid="maintenance-form-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todo">To Do</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Site</Label>
                  <Select value={dialog.form.site} onValueChange={(v) => setF("site", v)} disabled={!!restrictedKey}>
                    <SelectTrigger data-testid="maintenance-form-site"><SelectValue placeholder="Select site" /></SelectTrigger>
                    <SelectContent>{siteOpts.map((s) => <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Asset (optional)</Label>
                <Select value={dialog.form.asset_id} onValueChange={(v) => setF("asset_id", v)}>
                  <SelectTrigger data-testid="maintenance-form-asset"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific asset</SelectItem>
                    {dialogAssets.map((a) => <SelectItem key={a.id} value={a.id}>{a.asset_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Job Description</Label>
                <Textarea data-testid="maintenance-form-desc" rows={3} value={dialog.form.job_description} onChange={(e) => setF("job_description", e.target.value)} placeholder="e.g. Plumber to fix toilet in block C" />
              </div>
              {dialog.form.status === "completed" ? (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label>Date Done</Label><Input data-testid="maintenance-form-date" type="date" value={dialog.form.date_completed} onChange={(e) => setF("date_completed", e.target.value)} /></div>
                  <div className="space-y-1"><Label>Performed By</Label><Input data-testid="maintenance-form-by" value={dialog.form.performed_by} onChange={(e) => setF("performed_by", e.target.value)} placeholder="Staff or contractor" /></div>
                </div>
              ) : (
                <p className="text-xs text-slate-400">Date done and who performed it are filled in when the job is marked Completed.</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button data-testid="maintenance-save" onClick={save} disabled={busy} className="bg-orange-600 hover:bg-orange-700">{busy ? "Saving..." : "Save Job"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
